import { SearchSuggestionsList } from '@iheartjane/menu';
import {
  Box,
  Button,
  DismissIcon,
  Flex,
  Modal,
  SearchField,
} from '@iheartjane/reefer';
import { Form } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { SearchManager } from '@jane/bloom/components/searchManager';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useDebounce } from '@jane/bloom/hooks/useDebounce';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useUserSearchQuery } from '@jane/bloom/hooks/useUserSearchQuery';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import { HelpMeChooseBanner } from './helpMeChooseBanner';
import { toSuggestionProduct } from './toSuggestionProduct';

import styles from './searchModal.module.css';

export function SearchModal() {
  const {
    hideSearchSuggestions,
    searchModalQuery,
    setSearchModalQuery,
    shouldOpenSearchModal,
  } = useUserSearchQuery();
  const debouncedQuery = useDebounce(searchModalQuery, 300);

  const { featureFlags, goldConfig, pinnedStore, shoppingPreferences } =
    useRootLoaderData();
  const { fulfillmentMode } = useStoreSwitcher();
  const customLanguage = useCustomLanguage();
  const generatePath = useFullDestination();

  const handleClose = () => {
    hideSearchSuggestions();
    setSearchModalQuery('');
  };

  return (
    <ClientOnly>
      {() => (
        <Modal
          onRequestClose={handleClose}
          open={shouldOpenSearchModal}
          overlayClose
          portalClassName="searchModalPortal"
          topOverride="40px"
          variant="dialogue"
        >
          <Modal.Content padding={false} style={{ width: '592px' }}>
            <Flex alignItems="center" gap={16} px={24} py={16}>
              <Form
                action="/search"
                className={styles['searchFieldForm']}
                method="get"
                name="Search Form"
                onSubmit={handleClose}
              >
                <SearchField
                  borderRadius="lg"
                  disableMobileInputStyling
                  isDebounced={false}
                  label="Search"
                  name={SEARCH_TERM_QUERY_KEY}
                  onChange={(val) => setSearchModalQuery(val)}
                  placeholder={`Search ${pinnedStore.name}`}
                  value={searchModalQuery}
                />
              </Form>
              <Button.Icon
                ariaLabel="Close search"
                icon={<DismissIcon />}
                onClick={handleClose}
              />
            </Flex>
            <Box maxHeight="424px" maxWidth="100%" overflowY="scroll">
              <SearchManager key={debouncedQuery} query={debouncedQuery}>
                {({ products, shouldFetchNextPage, totalProducts }) => (
                  <SearchSuggestionsList
                    isLoading={shouldFetchNextPage}
                    onSearchLinkClick={handleClose}
                    products={products.map((product) =>
                      toSuggestionProduct({
                        customLanguage,
                        featureFlags,
                        fulfillmentMode,
                        generatePath,
                        goldConfig,
                        hideDropdown: handleClose,
                        pinnedStore,
                        product,
                        shoppingPreferences,
                      })
                    )}
                    query={searchModalQuery}
                    searchUrl={generatePath(
                      `/search?${new URLSearchParams({
                        [SEARCH_TERM_QUERY_KEY]: searchModalQuery,
                      })}`
                    )}
                    totalProducts={totalProducts}
                  />
                )}
              </SearchManager>
            </Box>
            <Box p={12}>
              <HelpMeChooseBanner />
            </Box>
          </Modal.Content>
        </Modal>
      )}
    </ClientOnly>
  );
}
